import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Grid } from '../components/ui/Grid';
import { Swiper } from '../components/ui/Swiper';
import { LoadMore } from '../components/ui/LoadMore';
import { Headline } from '../components/ui/Headline';
import { format, startOfDay, endOfDay, addDays, endOfWeek, endOfMonth } from 'date-fns';
import { EventPanel } from '../components/EventPanel';
import { Filters } from '../components/Filters';
import { SEOHead } from '../components/SEOHead';
import { useLocation } from '../hooks/useLocation';
import { fetchEvents, fetchFeaturedEvents, type EventWithDetails } from '../services/api/events';
import { dateLocales } from '../i18n';

const ITEMS_PER_PAGE = 12;

interface GroupedEvents {
  [key: string]: EventWithDetails[];
}

function Agenda() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState<EventWithDetails[]>([]);
  const [featuredEvents, setFeaturedEvents] = useState<EventWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<string | null>(null);
  const [nearMeEnabled, setNearMeEnabled] = useState(false);
  const { coordinates, loading: locationLoading } = useLocation();
  const locale = dateLocales[i18n.language as keyof typeof dateLocales] || dateLocales.en;

  // Load featured events once on mount
  useEffect(() => {
    const loadFeaturedEvents = async () => {
      try {
        const featured = await fetchFeaturedEvents(5, coordinates);
        setFeaturedEvents(featured);
      } catch (error) {
        console.error('Error loading featured events:', error);
      }
    };

    loadFeaturedEvents();
  }, [coordinates]);

  const loadEvents = async (start = 0, replace = true) => {
    try {
      setLoadingMore(!replace);
      
      const { events: newEvents, totalCount } = await fetchEvents(
        start / ITEMS_PER_PAGE,
        ITEMS_PER_PAGE,
        searchQuery,
        startDate,
        endDate,
        coordinates,
        nearMeEnabled
      );

      setEvents(prev => replace ? newEvents : [...prev, ...newEvents]);
      setHasMore(totalCount ? start + ITEMS_PER_PAGE < totalCount : false);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(0);
    loadEvents(0);
  }, [searchQuery, startDate, endDate, nearMeEnabled]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadEvents(nextPage * ITEMS_PER_PAGE, false);
  };

  const handleDateRangeChange = (range: string) => {
    const today = new Date();
    const tomorrow = addDays(today, 1);

    setSelectedDateRange(range === selectedDateRange ? null : range);

    switch (range) {
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case 'tomorrow':
        setStartDate(tomorrow);
        setEndDate(tomorrow);
        break;
      case 'this-week':
        setStartDate(today);
        setEndDate(endOfWeek(today));
        break;
      case 'this-month':
        setStartDate(today);
        setEndDate(endOfMonth(today));
        break;
      default:
        setStartDate(null);
        setEndDate(null);
        break;
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStartDate(null);
    setEndDate(null);
    setSelectedDateRange(null);
    setNearMeEnabled(false);
  };

  // Group events by date
  const groupedEvents = events.reduce<GroupedEvents>((groups, event) => {
    const date = new Date(event.event_date);
    const dateKey = format(date, 'yyyy-MM');
    
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    
    groups[dateKey].push(event);
    return groups;
  }, {});

  // Generate structured data for events
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EventSeries",
    "name": "Konzerte in Deiner Nähe",
    "description": "Entdecke kommende Konzerte, Festivals und Musik-Events in der Schweiz",
    "url": "https://jamnation.ch/agenda",
    "location": {
      "@type": "Country",
      "name": "Switzerland"
    },
    "organizer": {
      "@type": "Organization",
      "name": "jamnation",
      "url": "https://jamnation.ch"
    },
    "subEvent": events.slice(0, 10).map(event => {
      const firstBand = event.bands?.[0] || event.band;
      return {
        "@type": "MusicEvent",
        "name": event.title,
        "description": event.description,
        "startDate": event.event_date,
        "location": {
          "@type": "Place",
          "name": event.location,
          ...(event.coordinates && {
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": event.coordinates.latitude,
              "longitude": event.coordinates.longitude
            }
          })
        },
        ...(firstBand && {
          "performer": {
            "@type": "MusicGroup",
            "name": firstBand.name,
            "url": `https://jamnation.ch/bands/${firstBand.slug}`
          }
        }),
        "url": firstBand
          ? `https://jamnation.ch/bands/${firstBand.slug}/events/${event.slug}`
          : `https://jamnation.ch/agenda#${event.slug}`
      };
    })
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title subtitle={t('agenda.subtitle')}>
          {t('agenda.title')}
        </Title>
        <div className="text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Konzerte in Deiner Nähe"
        description="Entdecke kommende Konzerte, Festivals und Musik-Events in der Schweiz. Finde Live-Musik in deiner Nähe und verpasse keine Auftritte deiner Lieblingsbands."
        keywords={[
          'Konzerte Schweiz',
          'Musik Events',
          'Live Musik',
          'Konzert Agenda',
          'Schweizer Konzerte',
          'Musik Veranstaltungen',
          'Band Auftritte',
          'Konzert Termine'
        ]}
        url="https://jamnation.ch/agenda"
        structuredData={structuredData}
      />

      <div className="container mx-auto px-6 py-8 md:py-24">
        <Title 
          subtitle={t('agenda.subtitle')}
        >
          {t('agenda.title')}
        </Title>

        {featuredEvents.length > 0 && (
          <div className="mb-24">
            <Headline title={t('home.sections.upcomingEvents')} />
            <div className="grid md:grid-cols-4 gap-8">
              {featuredEvents.map((event) => (
                <EventPanel key={event.id} event={event} nearMeEnabled={nearMeEnabled} />
              ))}
            </div>
          </div>
        )}

        <div className="max-w-4xl mx-auto mb-8">
          <div className="text-xs text-gray-400 mb-2">FILTER</div>
          <Filters
            searchPlaceholder={t('agenda.filters.searchPlaceholder')}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            filterGroups={[
              {
                name: t('agenda.filters.dateRange'),
                options: [
                  { value: 'today', label: t('agenda.filters.today') },
                  { value: 'tomorrow', label: t('agenda.filters.tomorrow') },
                  { value: 'this-week', label: t('agenda.filters.thisWeek') },
                  { value: 'this-month', label: t('agenda.filters.thisMonth') },
                ],
                selected: selectedDateRange ? [selectedDateRange] : [],
                onToggle: handleDateRangeChange,
              },
            ]}
            onClearAll={handleClearFilters}
            nearMeFilter={{
              enabled: nearMeEnabled,
              onToggle: () => setNearMeEnabled(!nearMeEnabled),
              available: !locationLoading && !!coordinates,
            }}
          />
        </div>

        <div className="space-y-12">
          {Object.entries(groupedEvents).map(([dateKey, dateEvents]) => (
            <div key={dateKey}>
              <div className="flex items-center space-x-4 mb-8">
                <div className="font-tilt-warp text-light text-xl font-medium">
                  {format(new Date(dateKey), 'MMMM yy', { locale })}
                </div>
              </div>

              <Grid cols={4}>
                {dateEvents.map((event) => (
                  <EventPanel key={event.id} event={event} nearMeEnabled={nearMeEnabled} />
                ))}
              </Grid>
            </div>
          ))}

          {Object.keys(groupedEvents).length === 0 && (
            <div className="text-center text-light/60 py-12">
              {t('agenda.noEvents')}
            </div>
          )}

          <LoadMore
            onClick={handleLoadMore}
            loading={loadingMore}
            hasMore={hasMore}
          />
        </div>
      </div>
    </>
  );
}

export default Agenda;